/**
 * Sapp's Fitness Navbar
 * Design: White background, crimson red accents, gold highlights, near-black text
 * Logo: Text-based logo in top-left
 * Nav: Bold uppercase links
 * CTA: "Book a Free Call" — crimson red button
 */
import { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { contactFormSelector, scrollToContactForm } from "@/lib/contactFormNavigation";
import { desktopNavigationQuery } from "@/lib/mobileExperience";
import { scrollToSection } from "@/lib/sectionNavigation";

const navLinks = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Programs", href: "#programs" },
  { label: "Nutrition", href: "#nutrition" },
  { label: "Results", href: "#results" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

// Brand colors
const RED = "oklch(0.42 0.20 22)";
const RED_HOVER = "oklch(0.55 0.20 22)";
const GOLD = "oklch(0.80 0.17 80)";
const BLACK = "oklch(0.14 0.02 40)";
const DARK_TEXT = "oklch(0.22 0.02 40)";
const RED_PALE = "oklch(0.95 0.03 22)";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const desktopNavigation = window.matchMedia(desktopNavigationQuery);
    const closeOnDesktop = () => {
      if (desktopNavigation.matches) setMenuOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };

    closeOnDesktop();
    desktopNavigation.addEventListener("change", closeOnDesktop);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      desktopNavigation.removeEventListener("change", closeOnDesktop);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  const handleNavClick = (href: string) => {
    const navigateToSection = () => {
      if (href === contactFormSelector) {
        scrollToContactForm();
        return;
      }
      scrollToSection(href);
    };

    if (menuOpen) {
      setMenuOpen(false);
      // The expanded menu changes document height while it closes. Reapply the
      // destination after that short transition so the fixed header never hides
      // the chosen section on a phone.
      window.setTimeout(navigateToSection, 240);
      window.setTimeout(navigateToSection, 520);
      return;
    }

    navigateToSection();
  };

  return (
    <nav
      id="site-header"
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        background: "white",
        borderBottom: scrolled
          ? `4px solid ${RED}`
          : `4px solid oklch(0.90 0.008 40)`,
        boxShadow: scrolled
          ? `0 4px 28px oklch(0.42 0.20 22 / 0.18)`
          : "0 2px 12px rgba(0,0,0,0.06)",
      }}
    >
      <div style={{ width: "100%", padding: "0 4%", boxSizing: "border-box" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: "88px" }}>

          {/* Logo */}
          <button
            type="button"
            onClick={() => handleNavClick("#home")}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "0",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
              lineHeight: 1,
            }}
          >
            <span style={{
              fontFamily: "'Barlow Condensed', sans-serif",
              fontSize: "1.55rem",
              fontWeight: 900,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: RED,
              display: "block",
            }}>
              Sapp's Fitness
            </span>
            <span style={{
              fontFamily: "'Barlow Condensed', sans-serif",
              fontSize: "0.72rem",
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: DARK_TEXT,
              display: "block",
              marginTop: "2px",
            }}>
              One on One Nutrition &amp; Fitness
            </span>
          </button>

          {/* Desktop Nav Links */}
          <div className="hidden lg:flex items-center gap-0.5">
            {navLinks.map((link) => (
              <button
                type="button"
                key={link.href}
                onClick={() => handleNavClick(link.href)}
                style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontSize: "1.05rem",
                  fontWeight: 800,
                  letterSpacing: "0.10em",
                  textTransform: "uppercase",
                  color: DARK_TEXT,
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: "8px 12px",
                  borderRadius: "6px",
                  transition: "color 0.15s, background 0.15s",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.color = RED;
                  (e.currentTarget as HTMLElement).style.background = RED_PALE;
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.color = DARK_TEXT;
                  (e.currentTarget as HTMLElement).style.background = "transparent";
                }}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* Phone + CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:+12055830218"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "7px",
                fontFamily: "'Barlow Condensed', sans-serif",
                fontSize: "1.15rem",
                fontWeight: 800,
                letterSpacing: "0.04em",
                color: DARK_TEXT,
                textDecoration: "none",
                whiteSpace: "nowrap",
                transition: "color 0.15s",
              }}
              onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = RED)}
              onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = DARK_TEXT)}
            >
              <Phone size={18} style={{ color: RED, flexShrink: 0 }} />
              (205) 583-0218
            </a>
            <button
              type="button"
              onClick={() => handleNavClick("#contact")}
              style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                fontSize: "1.1rem",
                fontWeight: 900,
                letterSpacing: "0.10em",
                textTransform: "uppercase",
                background: RED,
                color: "white",
                border: "none",
                borderRadius: "8px",
                padding: "12px 24px",
                cursor: "pointer",
                whiteSpace: "nowrap",
                boxShadow: `0 3px 12px oklch(0.42 0.20 22 / 0.40)`,
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background = RED_HOVER;
                (e.currentTarget as HTMLElement).style.transform = "translateY(-2px)";
                (e.currentTarget as HTMLElement).style.boxShadow = `0 6px 20px oklch(0.42 0.20 22 / 0.50)`;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background = RED;
                (e.currentTarget as HTMLElement).style.transform = "translateY(0)";
                (e.currentTarget as HTMLElement).style.boxShadow = `0 3px 12px oklch(0.42 0.20 22 / 0.40)`;
              }}
            >
              Book a Free Call
            </button>
          </div>

          {/* Mobile Toggle */}
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            className="lg:hidden"
            style={{
              padding: "8px",
              borderRadius: "8px",
              background: "none",
              border: "none",
              cursor: "pointer",
              color: BLACK,
            }}
            aria-label={menuOpen ? "Close site navigation" : "Open site navigation"}
            aria-expanded={menuOpen}
            aria-controls="mobile-site-navigation"
          >
            {menuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        id="mobile-site-navigation"
        className={`lg:hidden transition-[max-height,opacity] duration-200 ${
          menuOpen
            ? "max-h-[calc(100dvh-72px)] overflow-y-auto opacity-100"
            : "max-h-0 overflow-hidden opacity-0 pointer-events-none"
        }`}
        style={{
          background: "white",
          borderTop: menuOpen ? `2px solid oklch(0.42 0.20 22 / 0.2)` : "none",
          overscrollBehavior: "contain",
        }}
      >
        <div style={{ padding: "12px 16px 16px", display: "flex", flexDirection: "column", gap: "4px" }}>
          {navLinks.map((link) => (
            <button
              type="button"
              key={link.href}
              onClick={() => handleNavClick(link.href)}
              style={{
                textAlign: "left",
                fontFamily: "'Barlow Condensed', sans-serif",
                fontSize: "1.3rem",
                fontWeight: 800,
                letterSpacing: "0.09em",
                textTransform: "uppercase",
                color: DARK_TEXT,
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: "12px 16px",
                borderRadius: "8px",
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background = RED_PALE;
                (e.currentTarget as HTMLElement).style.color = RED;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background = "transparent";
                (e.currentTarget as HTMLElement).style.color = DARK_TEXT;
              }}
            >
              {link.label}
            </button>
          ))}

          <button
            type="button"
            onClick={() => handleNavClick("#contact")}
            style={{
              fontFamily: "'Barlow Condensed', sans-serif",
              fontSize: "1.2rem",
              fontWeight: 900,
              letterSpacing: "0.09em",
              textTransform: "uppercase",
              background: RED,
              color: "white",
              border: "none",
              borderRadius: "8px",
              padding: "16px",
              cursor: "pointer",
              marginTop: "8px",
              boxShadow: `0 3px 12px oklch(0.42 0.20 22 / 0.35)`,
            }}
          >
            Book a Free Call
          </button>
          <a
            href="tel:+12055830218"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontFamily: "'Barlow Condensed', sans-serif",
              fontSize: "1.2rem",
              fontWeight: 800,
              color: DARK_TEXT,
              textDecoration: "none",
              padding: "10px 16px",
              marginTop: "4px",
              borderTop: `1px solid oklch(0.90 0.008 40)`,
            }}
          >
            <Phone size={18} style={{ color: RED }} />
            (205) 583-0218
          </a>
        </div>
      </div>
    </nav>
  );
}
